import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Zap, Instagram, Facebook, Music2, MessageCircle, Mail, MessageSquareText, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

function SmsSignup() {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/sms/subscribe", { phone: phone.trim(), name: name.trim() || undefined });
      setDone(true);
      toast.success("You're on the alert list");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="border border-yellow-400/30 bg-yellow-400/5 p-4 text-sm text-zinc-300" data-testid="sms-signup-success">
        <span className="text-yellow-400 font-mono uppercase tracking-wider text-xs block mb-1">Confirmed</span>
        You'll get chase-day and event alerts by text. Reply STOP anytime to unsubscribe.
      </div>
    );
  }

  return (
    <form onSubmit={subscribe} className="space-y-2" data-testid="sms-signup-form">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Name (optional)"
        className="w-full bg-[#0d0f12] border border-white/10 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-yellow-400/60 focus:outline-none"
        data-testid="sms-signup-name"
      />
      <div className="flex gap-2">
        <input
          type="tel"
          required
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+1 256 555 0123"
          className="flex-1 bg-[#0d0f12] border border-white/10 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-yellow-400/60 focus:outline-none font-mono"
          data-testid="sms-signup-phone"
        />
        <button
          type="submit"
          disabled={busy}
          className="px-4 py-2 bg-yellow-400 text-black text-xs font-mono uppercase tracking-wider font-bold hover:bg-yellow-300 disabled:opacity-50 flex items-center gap-2"
          data-testid="sms-signup-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign Up"}
        </button>
      </div>
      <p className="text-[10px] text-zinc-600 font-mono">Use international format (+1...). Msg rates may apply. Reply STOP to opt out.</p>
    </form>
  );
}

export default function Footer() {
  return (
    <footer className="border-t border-white/5 bg-[#0A0A0A]" data-testid="site-footer">
      <div className="container-vg py-16 grid grid-cols-1 md:grid-cols-5 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center justify-center w-9 h-9 border border-yellow-400/40 bg-yellow-400/5">
              <Zap className="w-5 h-5 text-yellow-400" strokeWidth={2.5} />
            </span>
            <div className="font-heading text-white text-lg font-black uppercase tracking-tight">Vortex Ghost</div>
          </div>
          <p className="text-zinc-400 max-w-md leading-relaxed">
            Severe weather chasing and research team operating across Tornado Alley and Dixie Alley.
            Documenting, measuring, and learning from nature's most violent storms — so others stay safe.
          </p>
          <div className="overline mt-6">Status</div>
          <div className="mt-2 flex items-center gap-2 text-sm font-mono">
            <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse-yellow" />
            <span className="text-zinc-300">Operational &middot; Monitoring active cells</span>
          </div>
        </div>

        <div>
          <div className="overline mb-4">Navigate</div>
          <ul className="space-y-2 text-zinc-400">
            <li><Link to="/about" className="hover:text-yellow-400">About</Link></li>
            <li><Link to="/radar" className="hover:text-yellow-400">Live Radar</Link></li>
            <li><Link to="/spc" className="hover:text-yellow-400">SPC Outlook</Link></li>
            <li><Link to="/chat" className="hover:text-yellow-400">Live Chat</Link></li>
            <li><Link to="/research" className="hover:text-yellow-400">Research</Link></li>
            <li><Link to="/shop" className="hover:text-yellow-400">Shop</Link></li>
            <li><Link to="/submit-photo" className="hover:text-yellow-400">Submit Photo</Link></li>
            <li><Link to="/apply" className="hover:text-yellow-400">Apply</Link></li>
            <li><Link to="/events" className="hover:text-yellow-400">Events</Link></li>
          </ul>
        </div>

        <div>
          <div className="overline mb-4">Connect</div>
          <ul className="space-y-2 text-zinc-400 text-sm">
            <li className="flex items-center gap-2 break-all"><Mail className="w-4 h-4 text-yellow-400 shrink-0" /> vortexspotter@vortexghost.business</li>
            <li className="font-mono">+1 (256) 404-3789</li>
          </ul>
          <div className="flex items-center gap-3 mt-5">
            <a href="https://instagram.com/vortexghosttt" target="_blank" rel="noopener noreferrer" className="w-9 h-9 inline-flex items-center justify-center border border-white/10 hover:border-yellow-400 hover:text-yellow-400 text-zinc-400" data-testid="social-instagram" aria-label="Instagram"><Instagram className="w-4 h-4" /></a>
            <a href="#" className="w-9 h-9 inline-flex items-center justify-center border border-white/10 hover:border-yellow-400 hover:text-yellow-400 text-zinc-400" data-testid="social-facebook" aria-label="Facebook"><Facebook className="w-4 h-4" /></a>
            <a href="#" className="w-9 h-9 inline-flex items-center justify-center border border-white/10 hover:border-yellow-400 hover:text-yellow-400 text-zinc-400" data-testid="social-tiktok" aria-label="TikTok"><Music2 className="w-4 h-4" /></a>
            <a href="#" className="w-9 h-9 inline-flex items-center justify-center border border-white/10 hover:border-yellow-400 hover:text-yellow-400 text-zinc-400" data-testid="social-discord" aria-label="Discord"><MessageCircle className="w-4 h-4" /></a>
          </div>
        </div>
        <div>
          <div className="overline mb-4 flex items-center gap-2"><MessageSquareText className="w-4 h-4 text-yellow-400" /> Storm Alerts</div>
          <p className="text-zinc-500 text-xs mb-3 leading-relaxed">Chase-day &amp; event alerts straight to your phone.</p>
          <SmsSignup />
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="container-vg py-6 flex flex-col md:flex-row justify-between items-center gap-2 text-xs text-zinc-500 font-mono uppercase tracking-wider">
          <div>&copy; {new Date().getFullYear()} Vortex Ghost &middot; All rights reserved</div>
          <div className="flex items-center gap-4">
            <span>SAFETY FIRST</span>
            <span className="text-yellow-400">/</span>
            <span>SCIENCE ALWAYS</span>
            <span className="text-yellow-400">/</span>
            <Link to="/admin" className="hover:text-yellow-400" data-testid="footer-admin-link">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
